/*!
 * Adapted from "The Furrow - ASCIIxGPT - Concept 5" by The Furrow
 * (https://thefurrow.tv/project/asciixgpt). Changes: ported to a WebGL2 shader,
 * with a different pattern set, glyphs, colors, settings, and a carve around content.
 * This file is licensed under CC BY-NC-SA 4.0:
 * https://creativecommons.org/licenses/by-nc-sa/4.0/
 */

// The field's bands follow the scroll like a layer far behind the page: they move `PARALLAX` as
// far as the page, on a spring that lags a little and overshoots slightly when you stop.

/** How far the bands move, as a fraction of the scroll. */
export const PARALLAX = 0.4;

// The spring's natural frequency (in radians per second) and damping ratio. A ratio under 1
// overshoots: at 0.65, by about 7% of how far it lagged. During a steady scroll it lags about
// 0.14s (`2 * 0.65 / 9`) behind.
const OMEGA = 9;
const DAMPING = 0.65;
// The longest integration step, in seconds, so a long frame stays stable.
const MAX_STEP = 1 / 120;
// Within this distance (in CSS pixels) and speed (in CSS pixels per second) of its target, the
// spring snaps to rest on it.
const REST_DISTANCE = 0.05;
const REST_SPEED = 0.5;

/** The bands' offset in CSS pixels, as it follows its target. */
export class ParallaxSpring {
  position = 0;
  private velocity = 0;

  /** Puts the spring at rest on `target` right away. */
  settle(target: number): void {
    this.position = target;
    this.velocity = 0;
  }

  /** Moves toward `target` over `dt` seconds. Returns whether the position changed. */
  step(target: number, dt: number): boolean {
    if (this.position === target && this.velocity === 0) return false;
    const steps = Math.max(1, Math.ceil(dt / MAX_STEP));
    const h = dt / steps;
    for (let i = 0; i < steps; i++) {
      const accel = -OMEGA * OMEGA * (this.position - target) - 2 * DAMPING * OMEGA * this.velocity;
      this.velocity += accel * h;
      this.position += this.velocity * h;
    }
    if (Math.abs(this.position - target) < REST_DISTANCE && Math.abs(this.velocity) < REST_SPEED)
      this.settle(target);
    return true;
  }
}
